import React, { useState, useRef, useEffect, useCallback } from 'react';

interface TooltipProps {
  children: React.ReactElement;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

// Base styles for tooltip - always use fixed positioning to prevent layout shifts
const BASE_TOOLTIP_STYLE: React.CSSProperties = {
  position: 'fixed',
  backgroundColor: '#1f2937',
  color: '#ffffff',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)',
  whiteSpace: 'nowrap',
  zIndex: 99999,
  padding: '4px 8px',
  fontSize: '12px',
  fontWeight: 500,
  borderRadius: '4px',
  pointerEvents: 'none',
};

/**
 * Custom Tooltip component with better styling than native title attribute
 * Wraps children and shows tooltip on hover
 * Uses fixed positioning to prevent overflow and scrollbars
 */
export const Tooltip: React.FC<TooltipProps> = ({ 
  children, 
  content, 
  position = 'bottom' 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: -9999, left: -9999 });
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const calculatePosition = useCallback(() => {
    if (!containerRef.current || !tooltipRef.current) return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const padding = 8; // Distance from edge of screen
    const gap = 6; // Gap between element and tooltip
    
    let top = 0;
    let left = 0;
    
    switch (position) {
      case 'bottom':
        top = containerRect.bottom + gap;
        left = containerRect.left + (containerRect.width / 2) - (tooltipRect.width / 2);
        break;
      case 'top':
        top = containerRect.top - tooltipRect.height - gap;
        left = containerRect.left + (containerRect.width / 2) - (tooltipRect.width / 2);
        break;
      case 'left':
        top = containerRect.top + (containerRect.height / 2) - (tooltipRect.height / 2);
        left = containerRect.left - tooltipRect.width - gap;
        break;
      case 'right':
        top = containerRect.top + (containerRect.height / 2) - (tooltipRect.height / 2);
        left = containerRect.right + gap;
        break;
    }
    
    // Prevent overflow on right edge
    if (left + tooltipRect.width > window.innerWidth - padding) {
      left = window.innerWidth - tooltipRect.width - padding;
    }
    // Prevent overflow on left edge
    if (left < padding) {
      left = padding;
    }
    // Prevent overflow on bottom edge
    if (top + tooltipRect.height > window.innerHeight - padding) {
      top = window.innerHeight - tooltipRect.height - padding;
    }
    // Prevent overflow on top edge
    if (top < padding) {
      top = padding;
    }
    
    setTooltipPosition({ top, left });
    setIsPositioned(true);
  }, [position]);

  const showTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(true);
    setIsPositioned(false); // Reset positioning state for fresh calculation
  };

  const hideTooltip = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      setIsPositioned(false);
    }, 100);
  };

  useEffect(() => {
    if (isVisible && !isPositioned) {
      // Use requestAnimationFrame to ensure tooltip is rendered before calculating position
      requestAnimationFrame(() => {
        calculatePosition();
      });
    }
  }, [isVisible, isPositioned, calculatePosition]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Clone child element and add event handlers
  const childWithHandlers = React.cloneElement(children, {
    onMouseEnter: (e: React.MouseEvent) => {
      showTooltip();
      if (children.props.onMouseEnter) {
        children.props.onMouseEnter(e);
      }
    },
    onMouseLeave: (e: React.MouseEvent) => {
      hideTooltip();
      if (children.props.onMouseLeave) {
        children.props.onMouseLeave(e);
      }
    },
  });

  // Combine base styles with calculated position
  // Use visibility: hidden until positioned to prevent layout jumps
  const tooltipStyle: React.CSSProperties = {
    ...BASE_TOOLTIP_STYLE,
    top: `${tooltipPosition.top}px`,
    left: `${tooltipPosition.left}px`,
    visibility: isPositioned ? 'visible' : 'hidden',
    opacity: isPositioned ? 1 : 0,
  };

  return (
    <div ref={containerRef} className="relative inline-block">
      {childWithHandlers}
      {isVisible && content && (
        <div
          ref={tooltipRef}
          style={tooltipStyle}
          role="tooltip"
        >
          {content}
        </div>
      )}
    </div>
  );
};

export default Tooltip;

